import { type RefObject, useLayoutEffect } from 'react'
import type { ShapeBounds } from '@/lib/shapes/types/shape-bounds'

/**
 * Measures the rendered path (`getBBox`) and reports it upward whenever the outline or the shape's
 * size changes. Skips non-finite measurements (e.g. an element not yet laid out).
 */
export function useReportedShapeBounds(
  pathRef: RefObject<SVGPathElement | null>,
  shapeId: string,
  shapePath: string,
  size: { width: number; height: number },
  onBoundsChange: ((shapeId: string, bounds: ShapeBounds) => void) | undefined
): void {
  const { width, height } = size

  useLayoutEffect(() => {
    if (!onBoundsChange || !pathRef.current) {
      return
    }

    const bbox = pathRef.current.getBBox()

    if (!Number.isFinite(bbox.width) || !Number.isFinite(bbox.height)) {
      return
    }

    onBoundsChange(shapeId, { x: bbox.x, y: bbox.y, width: bbox.width, height: bbox.height })
    // `shapePath`/`width`/`height` aren't read here: they're the re-measure triggers.
  }, [pathRef, onBoundsChange, shapeId, shapePath, width, height])
}
